// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THMenu } from '@Molecules/THMenu/THMenu';
import { FormItemProps, Space } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import React, { ReactElement, useMemo, useState } from 'react';
import { THButton } from '../THButton/THButton';
import { THDropdown } from '../THDropdown/THDropdown';
import { TemplateHelpType, templates } from '../THTextArea/Helper';
import { THInput } from './THInput';

export interface HelperProps extends FormItemProps {
    placeholder?: string;
    triggerElem?: ReactElement;
    onChange?: () => void;
    extraAction?: ReactElement;
    addonBefore?: ReactElement;
    completeItemName?: NamePath;
    extraFields?: TemplateHelpType[];
    autoComplete?: string;
}

export const Helper = ({
    placeholder,
    triggerElem,
    onChange,
    extraAction,
    addonBefore,
    completeItemName,
    extraFields = [],
    autoComplete,
    ...itemProps
}: HelperProps): ReactElement => {
    const i = useLocalMessage();

    const [{ start, end }, setSelection] = useState<{ start: number | null; end: number | null }>({
        start: null,
        end: null,
    });

    const templateVariables = useMemo(
        () => [...(extraFields || []), ...templates].sort((a, b) => a.name.localeCompare(b.name)),
        [extraFields],
    );

    return (
        <THForm.Item<any> noStyle shouldUpdate>
            {({ getFieldValue, setFields }) => (
                <THForm.Item
                    extraAction={
                        <Space>
                            {extraAction}

                            <THDropdown
                                triggerElem={() =>
                                    triggerElem || (
                                        <THButton className="template-helper__menu-btn" type="link">
                                            {i('notification.notifiers.template.extra-action')}
                                        </THButton>
                                    )
                                }
                                trigger={['click']}
                                menuClassName="template-helper__menu"
                            >
                                {templateVariables.map(({ name, help }) => (
                                    <THMenu.Item
                                        onClick={() => {
                                            const value = (getFieldValue(completeItemName || itemProps.name || []) ||
                                                '') as string;

                                            const nameFormatted = `{{${name}}}`;
                                            let valueParsed = '';

                                            if (start !== null && end !== null) {
                                                valueParsed =
                                                    value.substring(0, start) +
                                                    nameFormatted +
                                                    value.substring(end, value.length);
                                            } else {
                                                valueParsed = value + nameFormatted;
                                            }

                                            setFields([
                                                {
                                                    name: completeItemName || itemProps.name || [],
                                                    value: valueParsed,
                                                    touched: true,
                                                },
                                            ]);

                                            onChange?.();
                                        }}
                                        key={name}
                                    >
                                        <div className="template-helper__menu-item">
                                            <span className="template-helper__name">{name}</span>
                                            {help && <span className="template-helper__help">{help}</span>}
                                        </div>
                                    </THMenu.Item>
                                ))}
                            </THDropdown>
                        </Space>
                    }
                    {...itemProps}
                >
                    <THInput
                        autoComplete={autoComplete}
                        addonBefore={addonBefore}
                        onSelect={(event) => {
                            setSelection({
                                start: event.currentTarget.selectionStart,
                                end: event.currentTarget.selectionEnd,
                            });
                        }}
                        placeholder={placeholder}
                    />
                </THForm.Item>
            )}
        </THForm.Item>
    );
};
